import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  useAddUserMutation,
  useGetValidateSponsorIdQuery,
} from "../../../../Services/userApi.js";
import { Notification } from "../../../../components/ToastNotification/index.js";
import "./JoinUs.css";

const COUNTRIES = [
  "Pakistan",
  "Saudi Arabia",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
  "Canada",
  "Australia",
  "Qatar",
  "Kuwait",
  "Germany",
  "Other",
];

const REGIONS = [
  "Sindh",
  "Balochistan ",
  "Panjab",
  "KPK",
  "Gilgit Baltistan",
  "AJK",
];
const JoinUsForm = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const sponsorid = new URLSearchParams(search).get("sponsorid");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [sponsorName, setSponsorName] = useState("");
  const [sponError, setSponError] = useState("");
  const [charCount, setCharCount] = useState(0); // for any textarea if added

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone: "",
    country: "",
    city: "",
    region: "",
    password: "",
    confirmPassword: "",
    sponsorId: sponsorid || "",
    sponsorName: "",
    agreeTerms: false,
  });

  /* ── API hooks ── */
  const [addUser, { data, error, isLoading }] = useAddUserMutation();

  const { data: sponsoridData, error: sponsoridError } =
    useGetValidateSponsorIdQuery(sponsorid || formData.sponsorId, {
      skip: !(sponsorid || formData.sponsorId),
    });

  /* ── Effects ── */
  useEffect(() => {
    if (data?.message) {
      Notification(data.message, "success");
      navigate("/login");
    } else if (error?.data?.message) {
      Notification(error.data.message, "error");
    }
  }, [error, data, navigate]);

  useEffect(() => {
    if (sponsoridError?.data?.message) {
      setSponError(sponsoridError.data.message);
      setSponsorName("");
      return;
    }
    if (sponsoridData?.name) {
      setSponError("");
      setSponsorName(sponsoridData.name);
      setFormData((p) => ({ ...p, sponsorName: sponsoridData.name }));
    }
  }, [sponsoridError, sponsoridData]);

  /* ── Handlers ── */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((p) => ({
      ...p,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const {
      email,
      username,
      password,
      confirmPassword,
      phone,
      country,
      city,
      region,
      sponsorId,
      agreeTerms,
    } = formData;
    // console.log("Form data is valid, submitting:", formData);
    const required = [
      { value: username, label: "Full Name" },
      { value: email, label: "Email" },
      { value: phone, label: "Phone" },
      { value: country, label: "Country" },
      { value: city, label: "City" },
      { value: password, label: "Password" },
      { value: sponsorId, label: "Sponsor ID" },
    ];
    for (const f of required) {
      if (!f.value?.trim()) {
        Notification(`${f.label} is required`, "error");
        return;
      }
    }
    if (!agreeTerms) {
      Notification("Please agree to the Terms & Conditions", "error");
      return;
    }
    if (password !== confirmPassword) {
      Notification("Passwords do not match", "error");
      return;
    }
    if (phone.length !== 11) {
      Notification("Phone number must be exactly 11 digits", "error");
      return;
    }
    if (/\s/.test(email)) {
      Notification("Email cannot contain spaces", "error");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      Notification("Please enter a valid email address", "error");
      return;
    }
    if (!region) {
      Notification("Please select a region", "error");
      return;
    }
    if (!country) {
      Notification("Please select a country", "error");
      return;
    }
    if (city.trim() === "") {
      Notification("City cannot be empty", "error");
      return;
    }
    console.log("Form data is valid, submitting:", formData);

    addUser({ ...formData, fullName: username, mobile: phone, role: "user" });
  };

  return (
    <div className="jw">
      {/* bokeh */}
      <div className="j-bokeh j-bokeh--1" aria-hidden="true" />
      <div className="j-bokeh j-bokeh--2" aria-hidden="true" />

      <div className="jc">
        <div className="jcard">
          {/* ── Logo ── */}
          <div className="j-logo">
            <div className="l-logo-circle">
              <img src="/logo.jpeg" alt="Grow Biz International" />
            </div>
            <div className="j-logo-text">
              Grow Biz <span>International</span>
            </div>
          </div>

          {/* ── Heading ── */}
          <div className="j-head">
            <h2>Create Your Account</h2>
            <div className="j-gline" />
            <p>
              Join thousands of members building passive income through
              Pakistan's most trusted investment network.
            </p>
          </div>

          {/* ── Form ── */}
          <form onSubmit={handleSubmit} noValidate>
            {/* Full Name */}
            <Field label="Full Name" required>
              <input
                className="j-input"
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="e.g. Ahmad Raza"
                autoComplete="name"
                required
              />
            </Field>

            {/* Email */}
            <Field label="Email Address" required>
              <input
                className="j-input"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </Field>

            {/* Phone */}
            <Field label="Phone Number" required>
              <input
                className="j-input"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+92 300 1234567"
                autoComplete="tel"
                maxLength={11}
                required
              />
            </Field>

            {/* Country + City (2-col) */}
            <div className="j-row">
              <Field label="Country" required>
                <select
                  className="j-input j-select"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                >
                  <option value="" disabled>
                    Select country
                  </option>
                  {COUNTRIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </Field>
              <Field label="City" required>
                <input
                  className="j-input"
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="e.g. Karachi"
                  required
                />
              </Field>
            </div>
            {/* Region + City (2-col) */}

            <Field label="Region" required>
              <select
                className="j-input j-select"
                name="region"
                value={formData.region}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Select region
                </option>
                {REGIONS.map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </Field>

            {/* Password */}
            <Field label="Password" required>
              <div className="j-pass-wrap">
                <input
                  className="j-input j-input--pass"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a strong password"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="j-eye"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
            </Field>

            {/* Confirm Password */}
            <Field label="Confirm Password" required>
              <div className="j-pass-wrap">
                <input
                  className="j-input j-input--pass"
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Repeat your password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="j-eye"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label="Toggle confirm password visibility"
                >
                  {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
            </Field>

            {/* Sponsor ID */}
            <Field label="Sponsor ID" required>
              <input
                className="j-input"
                type="text"
                name="sponsorId"
                value={formData.sponsorId}
                onChange={handleChange}
                placeholder="e.g. 938748"
              />
              {sponError && <p className="j-field-err">✕ {sponError}</p>}
              {sponsorName && (
                <p className="j-field-ok">✓ Sponsor: {sponsorName}</p>
              )}
            </Field>

            {/* Terms checkbox */}
            <label className="j-check" htmlFor="agreeTerms">
              <input
                type="checkbox"
                id="agreeTerms"
                name="agreeTerms"
                checked={formData.agreeTerms}
                onChange={handleChange}
              />
              <span>
                I agree to the{" "}
                <Link to="/termsconditions" className="j-terms-link">
                  Terms &amp; Conditions
                </Link>{" "}
                and Privacy Policy
              </span>
            </label>

            {/* Submit */}
            <button type="submit" className="j-submit" disabled={isLoading}>
              <span className="j-submit-inner">
                {isLoading && <span className="j-spinner" />}
                {isLoading ? "Creating Account..." : "Create Account"}
              </span>
            </button>

            {/* Login link */}
            <p className="j-login-link">
              Already have an account? <Link to="/login">Log in here</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

/* ── Field wrapper ── */
function Field({ label, required, children }) {
  return (
    <div className="j-group">
      <label className="j-label">
        {label} {required && <span className="j-req">*</span>}
      </label>
      <div className="j-input-wrap">{children}</div>
    </div>
  );
}

export default JoinUsForm;
